import React from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  Badge,
  Row,
  Col
} from "reactstrap"

const InfoModal = ({ isOpen, toggle, title, content }) => {
  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      centered
      style={{ maxHeight: '90vh' }}
      contentClassName="info-modal-content"
    >
      <ModalHeader toggle={toggle}>
        <i className="bx bx-info-circle me-2 text-info"></i>
        {title}
      </ModalHeader>
      <ModalBody
        style={{
          maxHeight: '70vh',
          overflowY: 'auto',
          padding: '1.5rem'
        }}
      >
        <div
          className="info-content"
          style={{
            fontSize: '0.9rem',
            lineHeight: '1.5'
          }}
        >
          {content}
        </div>
      </ModalBody>
      <ModalFooter style={{ borderTop: '1px solid #dee2e6', padding: '1rem 1.5rem' }}>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}

// Info content definitions for different sections
export const getInfoContent = (type) => {
  switch (type) {
    case 'dashboard_overview':
      return (
        <div>
          <h5 className="text-primary mb-3">Dashboard Overview - Summary Cards Calculation Logic</h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">
                <i className="bx bx-file-blank me-2"></i>Total RFQs
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Counts all records from <code>request_for_quotation_management</code> table</li>
                <li>Applies date range filter if specified</li>
                <li>Excludes soft-deleted records</li>
                <li>Represents the complete universe of RFQ requests</li>
              </ul>
              <Badge color="info" className="badge-soft">
                SQL: COUNT(*) FROM request_for_quotation_management WHERE created_at BETWEEN date_range
              </Badge>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-success mb-2">
                <i className="bx bx-check-circle me-2"></i>Active RFQs
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Counts RFQs with status indicating active processing</li>
                <li>Includes statuses: 'Open', 'In Progress', 'Under Review'</li>
                <li>Excludes completed, closed, or cancelled RFQs</li>
                <li>Represents RFQs currently requiring attention</li>
              </ul>
              <Badge color="info" className="badge-soft">
                SQL: COUNT(*) WHERE current_status_id IN (active_status_ids)
              </Badge>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-info mb-2">
                <i className="bx bx-user-check me-2"></i>Assigned RFQs
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Counts RFQs where <code>assigned_vendor_id</code> is not null</li>
                <li>Indicates RFQs that have been assigned to specific vendors</li>
                <li>Includes both active and completed assignments</li>
                <li>Helps track vendor allocation efficiency</li>
              </ul>
              <Badge color="info" className="badge-soft">
                SQL: COUNT(*) WHERE assigned_vendor_id IS NOT NULL
              </Badge>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-danger mb-2">
                <i className="bx bx-x-circle me-2"></i>Closed RFQs
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Counts RFQs with final closure status</li>
                <li>Includes statuses: 'Completed', 'Cancelled', 'Rejected'</li>
                <li>Represents RFQs that no longer require action</li>
                <li>Used for completion rate analysis</li>
              </ul>
              <Badge color="info" className="badge-soft">
                SQL: COUNT(*) WHERE current_status_id IN (closed_status_ids)
              </Badge>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-secondary mb-2">
                <i className="bx bx-refresh me-2"></i>Rework RFQs
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Counts RFQs requiring revision or rework</li>
                <li>Includes statuses: 'Rework Required', 'Revision Needed'</li>
                <li>Tracks quality issues and revision requests</li>
                <li>Helps identify process improvement opportunities</li>
              </ul>
              <Badge color="info" className="badge-soft">
                SQL: COUNT(*) WHERE current_status_id IN (rework_status_ids)
              </Badge>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">
                <i className="bx bx-user-plus me-2"></i>Vendor Invited RFQs
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Counts RFQs where vendors have been invited to bid</li>
                <li>Tracks invitation status and vendor engagement</li>
                <li>Includes both pending and responded invitations</li>
                <li>Measures vendor outreach effectiveness</li>
              </ul>
              <Badge color="info" className="badge-soft">
                SQL: COUNT(*) WHERE vendor_invitation_sent = 1
              </Badge>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-dark mb-2">
                <i className="bx bx-cog me-2"></i>Work in Progress RFQs
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Counts RFQs currently being actively worked on</li>
                <li>Includes statuses: 'In Progress', 'Under Development'</li>
                <li>Represents RFQs with ongoing activities</li>
                <li>Helps track current workload and capacity</li>
              </ul>
              <Badge color="info" className="badge-soft">
                SQL: COUNT(*) WHERE current_status_id IN (wip_status_ids)
              </Badge>
            </CardBody>
          </Card>

          <Card className="border-primary">
            <CardBody>
              <h6 className="text-primary mb-2">
                <i className="bx bx-info-circle me-2"></i>Summary Card Business Logic
              </h6>
              <p className="mb-2"><strong>Key Insights:</strong></p>
              <ul className="mb-0">
                <li><strong>Total RFQs:</strong> Complete count of all requests in the system</li>
                <li><strong>Active RFQs:</strong> Currently open and requiring attention</li>
                <li><strong>Assigned RFQs:</strong> Allocated to specific vendors for processing</li>
                <li><strong>Closed RFQs:</strong> Completed or terminated requests</li>
                <li><strong>Rework RFQs:</strong> Requiring revision or quality improvements</li>
                <li><strong>Vendor Invited:</strong> Vendors have been contacted for bids</li>
                <li><strong>Work in Progress:</strong> Currently being actively developed</li>
              </ul>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">
                <i className="bx bx-credit-card me-2"></i>Payment Summary Chart
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Aggregates payment data from milestone payments</li>
                <li>Groups by payment status (Paid, Pending, Overdue)</li>
                <li>Sums total amounts for each status</li>
                <li>Converts to Indian Rupees (₹)</li>
              </ul>
              <Badge color="info" className="badge-soft">
                SQL: SELECT payment_status, SUM(amount) FROM milestone_payments GROUP BY status
              </Badge>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <h6 className="text-info mb-2">
                <i className="bx bx-time me-2"></i>Recent Activities
              </h6>
              <p className="mb-2"><strong>Data Source:</strong></p>
              <ul className="mb-2">
                <li>Tracks recent RFQ status changes and updates</li>
                <li>Shows latest vendor assignments and bid submissions</li>
                <li>Displays milestone completions and payment updates</li>
                <li>Orders by timestamp in descending order (newest first)</li>
                <li>Limited to last 50 activities for performance</li>
              </ul>
              <Badge color="info" className="badge-soft">
                Real-time activity feed from RFQ management system
              </Badge>
            </CardBody>
          </Card>
        </div>
      )

    case 'status_analytics':
      return (
        <div>
          <h5 className="text-primary mb-3">Status Analytics - Data Calculation Logic</h5>
          
          <Card className="mb-3">
            <CardBody>
              <h6 className="text-info mb-2">
                <i className="bx bx-bar-chart-alt me-2"></i>Status Distribution Chart
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Joins <code>request_for_quotation_management</code> with <code>request_for_quotation_status_mst</code></li>
                <li>Groups by status name and counts occurrences</li>
                <li>Calculates average days in each status</li>
                <li>Sums budget and final amounts per status</li>
              </ul>
              <Badge color="info" className="badge-soft">
                Complex query with status transitions and time calculations
              </Badge>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-success mb-2">
                <i className="bx bx-line-chart me-2"></i>Status Timeline Chart
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Tracks status changes over time periods</li>
                <li>Groups by date/period and status</li>
                <li>Shows trend of status transitions</li>
                <li>Calculates moving averages</li>
              </ul>
              <Badge color="info" className="badge-soft">
                Time-series analysis with status change tracking
              </Badge>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <h6 className="text-warning mb-2">
                <i className="bx bx-table me-2"></i>Detailed Status Table
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Shows comprehensive status metrics</li>
                <li>Calculates percentage distribution</li>
                <li>Computes average time spent in each status</li>
                <li>Aggregates financial data per status</li>
              </ul>
              <Badge color="info" className="badge-soft">
                Multi-dimensional status analysis with financial metrics
              </Badge>
            </CardBody>
          </Card>
        </div>
      )

    case 'financial_insights':
      return (
        <div>
          <h5 className="text-primary mb-3">Financial Insights - Data Calculation Logic</h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-success mb-2">
                <i className="bx bx-money me-2"></i>Total Budget
              </h6>
              <p className="mb-2"><strong>Data Source & Calculation:</strong></p>
              <ul className="mb-2">
                <li><strong>Primary Table:</strong> <code>tbl_rfq_task_payment_milestone</code></li>
                <li><strong>Relationship:</strong> Links to <code>request_for_quotation_management</code> via <code>rfq_id</code></li>
                <li><strong>Calculation:</strong> SUM(DISTINCT milestone.payment_amount)</li>
                <li><strong>Logic:</strong> Aggregates all milestone payment amounts for each RFQ</li>
                <li>Applies date range and project filters</li>
                <li>Excludes cancelled/rejected RFQs</li>
                <li>Converts to Indian Rupees (₹)</li>
              </ul>
              <Badge color="info" className="badge-soft">
                SQL: SUM(DISTINCT milestone.payment_amount) FROM tbl_rfq_task_payment_milestone
              </Badge>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-info mb-2">
                <i className="bx bx-check-double me-2"></i>Total Paid Amount
              </h6>
              <p className="mb-2"><strong>Data Source & Calculation:</strong></p>
              <ul className="mb-2">
                <li><strong>Primary Tables:</strong> <code>tbl_rfq_task_payment_milestone</code> + <code>rfq_payment</code></li>
                <li><strong>Relationship:</strong> milestone.id = payment.milestone_id</li>
                <li><strong>Calculation:</strong> SUM(CASE WHEN payment.status = "paid" THEN payment.paid_amount ELSE 0 END)</li>
                <li><strong>Logic:</strong> Sums actual paid amounts from payment records</li>
                <li>Only includes payments with 'Paid' status</li>
                <li>Links payments to specific milestones</li>
                <li>Real-time updates with payment status changes</li>
              </ul>
              <Badge color="info" className="badge-soft">
                SQL: SUM(payment.paid_amount) FROM rfq_payment WHERE status = 'paid'
              </Badge>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-warning mb-2">
                <i className="bx bx-clock me-2"></i>Total Pending Amount
              </h6>
              <p className="mb-2"><strong>Data Source & Calculation:</strong></p>
              <ul className="mb-2">
                <li><strong>Primary Tables:</strong> <code>tbl_rfq_task_payment_milestone</code> + <code>rfq_payment</code></li>
                <li><strong>Relationship:</strong> milestone.id = payment.milestone_id</li>
                <li><strong>Calculation:</strong> SUM(CASE WHEN payment.status IN ("pending", "requested") THEN payment.amount_requested ELSE 0 END)</li>
                <li><strong>Logic:</strong> Sums requested amounts for pending payments</li>
                <li>Includes both 'pending' and 'requested' status payments</li>
                <li>Shows outstanding payment obligations</li>
                <li>Updates as payments are processed</li>
              </ul>
              <Badge color="info" className="badge-soft">
                SQL: SUM(payment.amount_requested) WHERE status IN ('pending', 'requested')
              </Badge>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">
                <i className="bx bx-trending-up me-2"></i>Budget Utilization
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Formula: (Total Paid Amount / Total Budget) × 100</li>
                <li>Shows percentage of milestone budget actually paid</li>
                <li>Values &gt; 100% indicate budget overrun</li>
                <li>Color-coded: Green (≤80%), Yellow (80-100%), Red (&gt;100%)</li>
                <li>Based on actual milestone payments vs planned amounts</li>
              </ul>
              <Badge color="info" className="badge-soft">
                Percentage: (total_paid / total_budget) * 100
              </Badge>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-success mb-2">
                <i className="bx bx-pie-chart-alt me-2"></i>Payment Completion Rate
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Formula: (Total Paid Amount / (Total Paid + Total Pending)) × 100</li>
                <li>Shows percentage of payment requests that are completed</li>
                <li>Tracks payment processing efficiency</li>
                <li>Updates in real-time with payment status changes</li>
                <li>Helps monitor cash flow and vendor payment cycles</li>
              </ul>
              <Badge color="info" className="badge-soft">
                Percentage: (paid_amount / (paid_amount + pending_amount)) * 100
              </Badge>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <h6 className="text-info mb-2">
                <i className="bx bx-line-chart me-2"></i>Payment Trends
              </h6>
              <p className="mb-2"><strong>Data Source & Calculation:</strong></p>
              <ul className="mb-2">
                <li><strong>Primary Table:</strong> <code>rfq_payment</code></li>
                <li><strong>Time Grouping:</strong> DATE_FORMAT(payment.payment_date, "%Y-%m")</li>
                <li><strong>Metrics:</strong> COUNT(*) as payment_count, SUM(amount_requested), SUM(paid_amount)</li>
                <li><strong>Logic:</strong> Groups payments by month and status</li>
                <li>Shows payment volume and amount trends over time</li>
                <li>Tracks both requested and actual paid amounts</li>
                <li>Helps identify seasonal payment patterns</li>
              </ul>
              <Badge color="info" className="badge-soft">
                Monthly aggregation with status-wise breakdown
              </Badge>
            </CardBody>
          </Card>
        </div>
      )

    case 'vendor_performance':
      return (
        <div>
          <h5 className="text-primary mb-3">Vendor Performance - Data Calculation Logic</h5>
          
          <Card className="mb-3">
            <CardBody>
              <h6 className="text-success mb-2">
                <i className="bx bx-user-check me-2"></i>Top Performers Chart
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Calculates completion rate: (Completed RFQs / Total Assigned) × 100</li>
                <li>Measures task completion efficiency</li>
                <li>Evaluates payment processing speed</li>
                <li>Ranks vendors by composite performance score</li>
              </ul>
              <Badge color="info" className="badge-soft">
                Multi-factor performance scoring algorithm
              </Badge>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-info mb-2">
                <i className="bx bx-time-five me-2"></i>Average Completion Time
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Measures time from RFQ assignment to completion</li>
                <li>Calculates: AVG(completion_date - assignment_date)</li>
                <li>Groups by vendor for comparison</li>
                <li>Excludes outliers and cancelled projects</li>
              </ul>
              <Badge color="info" className="badge-soft">
                SQL: AVG(DATEDIFF(completion_date, assignment_date)) GROUP BY vendor
              </Badge>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <h6 className="text-warning mb-2">
                <i className="bx bx-dollar-circle me-2"></i>Bid Acceptance Rate
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Formula: (Accepted Bids / Total Bids Submitted) × 100</li>
                <li>Indicates vendor competitiveness</li>
                <li>Tracks bid quality and pricing accuracy</li>
                <li>Helps identify reliable vendors</li>
              </ul>
              <Badge color="info" className="badge-soft">
                Percentage: (accepted_bids / total_bids) * 100
              </Badge>
            </CardBody>
          </Card>
        </div>
      )

    case 'project_analytics':
      return (
        <div>
          <h5 className="text-primary mb-3">Project Analytics - Data Calculation Logic</h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-success mb-2">
                <i className="bx bx-building me-2"></i>Project Performance Chart
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Groups RFQs by project_id and calculates metrics</li>
                <li>Completion Rate: (Completed RFQs / Total RFQs per Project) × 100</li>
                <li>Budget Utilization: (Final Amount / Budget) × 100</li>
                <li>Payment Completion: (Paid Amount / Final Amount) × 100</li>
              </ul>
              <Badge color="info" className="badge-soft">
                Multi-dimensional project performance analysis
              </Badge>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-info mb-2">
                <i className="bx bx-time me-2"></i>Project Timeline Analysis
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Tracks project progress over time periods</li>
                <li>Calculates average project duration</li>
                <li>Identifies bottlenecks and delays</li>
                <li>Compares planned vs actual timelines</li>
              </ul>
              <Badge color="info" className="badge-soft">
                Time-series analysis with milestone tracking
              </Badge>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <h6 className="text-warning mb-2">
                <i className="bx bx-money me-2"></i>Project Budget Analysis
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Aggregates all RFQ budgets per project</li>
                <li>Compares allocated vs utilized budgets</li>
                <li>Identifies cost overruns and savings</li>
                <li>Tracks payment completion rates</li>
              </ul>
              <Badge color="info" className="badge-soft">
                Financial performance analysis per project
              </Badge>
            </CardBody>
          </Card>
        </div>
      )

    case 'timeline_analytics':
      return (
        <div>
          <h5 className="text-primary mb-3">Timeline Analytics - Data Calculation Logic</h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-success mb-2">
                <i className="bx bx-task me-2"></i>Active Tasks Card
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Counts tasks from <code>tbl_task_for_rfq</code> where status ≠ 'completed'</li>
                <li>Joins with RFQ table to get active RFQs only</li>
                <li>Includes tasks with progress tracking</li>
                <li>Updates in real-time with task status changes</li>
              </ul>
              <Badge color="info" className="badge-soft">
                SQL: COUNT(*) FROM tbl_task_for_rfq WHERE status != 'completed'
              </Badge>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-info mb-2">
                <i className="bx bx-pie-chart-alt me-2"></i>Milestone Progress Distribution
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Groups milestones by status (In Progress, Completed, Pending)</li>
                <li>Counts milestones in each status category</li>
                <li>Calculates percentage distribution</li>
                <li>Links to task completion data</li>
              </ul>
              <Badge color="info" className="badge-soft">
                SQL: SELECT milestone_status, COUNT(*) FROM tbl_rfq_task_payment_milestone GROUP BY status
              </Badge>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <h6 className="text-warning mb-2">
                <i className="bx bx-trending-up me-2"></i>Task Progress Timeline
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Tracks progress updates over time periods</li>
                <li>Calculates average progress percentage</li>
                <li>Counts number of progress updates per period</li>
                <li>Shows trend of task completion rates</li>
              </ul>
              <Badge color="info" className="badge-soft">
                Time-series analysis of task progress with moving averages
              </Badge>
            </CardBody>
          </Card>
        </div>
      )

    case 'milestone_analytics':
      return (
        <div>
          <h5 className="text-primary mb-3">Milestone Analytics - Data Calculation Logic</h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-success mb-2">
                <i className="bx bx-check-circle me-2"></i>Milestone Completion Rate
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Formula: (Completed Milestones / Total Milestones) × 100</li>
                <li>Tracks milestone completion across all projects</li>
                <li>Updates with milestone status changes</li>
                <li>Filters by date range and project criteria</li>
              </ul>
              <Badge color="info" className="badge-soft">
                Percentage: (completed_milestones / total_milestones) * 100
              </Badge>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-info mb-2">
                <i className="bx bx-money me-2"></i>Payment Milestone Status
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Aggregates payment amounts by milestone status</li>
                <li>Tracks pending, approved, and paid milestones</li>
                <li>Calculates total payment values</li>
                <li>Shows cash flow and payment pipeline</li>
              </ul>
              <Badge color="info" className="badge-soft">
                SQL: SUM(payment_amount) GROUP BY milestone_status
              </Badge>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <h6 className="text-warning mb-2">
                <i className="bx bx-time-five me-2"></i>Average Milestone Duration
              </h6>
              <p className="mb-2"><strong>Calculation:</strong></p>
              <ul className="mb-2">
                <li>Measures time from milestone start to completion</li>
                <li>Calculates: AVG(completion_date - start_date)</li>
                <li>Groups by milestone type and project</li>
                <li>Identifies bottlenecks and delays</li>
              </ul>
              <Badge color="info" className="badge-soft">
                Time analysis: AVG(DATEDIFF(completion_date, start_date))
              </Badge>
            </CardBody>
          </Card>
        </div>
      )

    case 'total_rfqs':
      return (
        <div>
          <h5 className="text-primary mb-3">
            <i className="bx bx-file-blank me-2"></i>Total RFQs - Business Logic & Calculation
          </h5>

          <Card className="border-primary mb-3">
            <CardBody>
              <h6 className="text-primary mb-3">📊 Business Purpose</h6>
              <p className="mb-3">
                The <strong>Total RFQs</strong> metric provides a comprehensive count of all Request for Quotation
                records in the system, serving as the foundation metric for all other calculations and analytics.
              </p>

              <h6 className="text-success mb-2">🔢 Calculation Logic</h6>
              <ul className="mb-3">
                <li><strong>Data Source:</strong> <code>request_for_quotation_management</code> table</li>
                <li><strong>Count Method:</strong> Simple COUNT(*) aggregation</li>
                <li><strong>Date Filter:</strong> Applied based on <code>created_at</code> timestamp</li>
                <li><strong>Exclusions:</strong> Soft-deleted records (where <code>deleted_at</code> IS NULL)</li>
                <li><strong>Real-time:</strong> Updates automatically as new RFQs are created</li>
              </ul>

              <h6 className="text-info mb-2">💼 Business Impact</h6>
              <ul className="mb-3">
                <li><strong>Volume Tracking:</strong> Monitors overall RFQ request volume</li>
                <li><strong>Trend Analysis:</strong> Identifies seasonal patterns and growth trends</li>
                <li><strong>Capacity Planning:</strong> Helps determine resource allocation needs</li>
                <li><strong>Performance Baseline:</strong> Foundation for calculating success rates</li>
              </ul>

              <Badge color="primary" className="badge-soft mb-2">
                SQL: SELECT COUNT(*) FROM request_for_quotation_management WHERE created_at BETWEEN ? AND ? AND deleted_at IS NULL
              </Badge>
            </CardBody>
          </Card>

          <Card className="border-light">
            <CardBody>
              <h6 className="text-warning mb-2">⚠️ Key Considerations</h6>
              <ul className="mb-0">
                <li>Includes all RFQ statuses (active, closed, cancelled)</li>
                <li>Date range filtering affects historical comparisons</li>
                <li>Soft-deleted records are excluded from count</li>
                <li>Real-time updates may cause slight variations during peak hours</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'active_rfqs':
      return (
        <div>
          <h5 className="text-success mb-3">
            <i className="bx bx-check-circle me-2"></i>Active RFQs - Business Logic & Calculation
          </h5>

          <Card className="border-success mb-3">
            <CardBody>
              <h6 className="text-success mb-3">📊 Business Purpose</h6>
              <p className="mb-3">
                The <strong>Active RFQs</strong> metric tracks all RFQ requests that are currently open and
                require attention, helping teams prioritize workload and manage ongoing projects effectively.
              </p>

              <h6 className="text-primary mb-2">🔢 Calculation Logic</h6>
              <ul className="mb-3">
                <li><strong>Status Filter:</strong> Includes specific active status IDs</li>
                <li><strong>Active Statuses:</strong> 'Open', 'In Progress', 'Under Review', 'Pending Approval'</li>
                <li><strong>Join Tables:</strong> Links with <code>request_for_quotation_status_mst</code></li>
                <li><strong>Exclusions:</strong> Completed, closed, cancelled, and on-hold RFQs</li>
                <li><strong>Real-time:</strong> Updates when status changes occur</li>
              </ul>

              <h6 className="text-info mb-2">💼 Business Impact</h6>
              <ul className="mb-3">
                <li><strong>Workload Management:</strong> Shows current active workload</li>
                <li><strong>Resource Allocation:</strong> Helps distribute team capacity</li>
                <li><strong>Priority Setting:</strong> Identifies urgent items needing attention</li>
                <li><strong>Bottleneck Detection:</strong> Highlights process delays</li>
              </ul>

              <Badge color="success" className="badge-soft mb-2">
                SQL: SELECT COUNT(*) WHERE current_status_id IN (1,2,3,4) AND deleted_at IS NULL
              </Badge>
            </CardBody>
          </Card>

          <Card className="border-light">
            <CardBody>
              <h6 className="text-warning mb-2">⚠️ Key Considerations</h6>
              <ul className="mb-0">
                <li>Status definitions may vary by organization workflow</li>
                <li>High active count may indicate process bottlenecks</li>
                <li>Should be monitored against team capacity</li>
                <li>Includes RFQs at various stages of completion</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'assigned_rfqs':
      return (
        <div>
          <h5 className="text-info mb-3">
            <i className="bx bx-user-check me-2"></i>Assigned RFQs - Business Logic & Calculation
          </h5>

          <Card className="border-info mb-3">
            <CardBody>
              <h6 className="text-info mb-3">📊 Business Purpose</h6>
              <p className="mb-3">
                The <strong>Assigned RFQs</strong> metric tracks RFQ requests that have been allocated to specific
                vendors, measuring vendor engagement and assignment efficiency in the procurement process.
              </p>

              <h6 className="text-primary mb-2">🔢 Calculation Logic</h6>
              <ul className="mb-3">
                <li><strong>Assignment Field:</strong> <code>assigned_vendor_id</code> IS NOT NULL</li>
                <li><strong>Vendor Linking:</strong> Joins with vendor management tables</li>
                <li><strong>Status Independent:</strong> Includes both active and completed assignments</li>
                <li><strong>Date Filtering:</strong> Based on assignment date or creation date</li>
                <li><strong>Validation:</strong> Ensures vendor ID exists in vendor master table</li>
              </ul>

              <h6 className="text-success mb-2">💼 Business Impact</h6>
              <ul className="mb-3">
                <li><strong>Vendor Utilization:</strong> Tracks how many RFQs are vendor-assigned</li>
                <li><strong>Assignment Rate:</strong> Percentage of total RFQs that get assigned</li>
                <li><strong>Vendor Performance:</strong> Foundation for vendor evaluation metrics</li>
                <li><strong>Process Efficiency:</strong> Measures assignment workflow effectiveness</li>
              </ul>

              <Badge color="info" className="badge-soft mb-2">
                SQL: SELECT COUNT(*) WHERE assigned_vendor_id IS NOT NULL AND deleted_at IS NULL
              </Badge>
            </CardBody>
          </Card>

          <Card className="border-light">
            <CardBody>
              <h6 className="text-warning mb-2">⚠️ Key Considerations</h6>
              <ul className="mb-0">
                <li>Assignment doesn't guarantee vendor response</li>
                <li>Multiple vendors may be assigned to single RFQ</li>
                <li>Assignment date vs. creation date filtering differences</li>
                <li>Vendor deactivation may affect historical counts</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'closed_rfqs':
      return (
        <div>
          <h5 className="text-danger mb-3">
            <i className="bx bx-x-circle me-2"></i>Closed RFQs - Business Logic & Calculation
          </h5>

          <Card className="border-danger mb-3">
            <CardBody>
              <h6 className="text-danger mb-3">📊 Business Purpose</h6>
              <p className="mb-3">
                The <strong>Closed RFQs</strong> metric represents all RFQ requests that have reached a final state,
                whether completed successfully, cancelled, or rejected, providing closure rate analytics.
              </p>

              <h6 className="text-primary mb-2">🔢 Calculation Logic</h6>
              <ul className="mb-3">
                <li><strong>Final Statuses:</strong> 'Completed', 'Cancelled', 'Rejected', 'Expired'</li>
                <li><strong>Status Mapping:</strong> Uses predefined closed status ID list</li>
                <li><strong>Completion Date:</strong> Tracks when RFQ reached final state</li>
                <li><strong>Reason Codes:</strong> May include closure reason categorization</li>
                <li><strong>Audit Trail:</strong> Maintains history of status changes</li>
              </ul>

              <h6 className="text-success mb-2">💼 Business Impact</h6>
              <ul className="mb-3">
                <li><strong>Completion Rate:</strong> Percentage of RFQs that reach closure</li>
                <li><strong>Success Analysis:</strong> Breakdown of completion vs. cancellation</li>
                <li><strong>Process Efficiency:</strong> Time-to-closure metrics</li>
                <li><strong>Resource Planning:</strong> Historical closure patterns for forecasting</li>
              </ul>

              <Badge color="danger" className="badge-soft mb-2">
                SQL: SELECT COUNT(*) WHERE current_status_id IN (closed_status_ids) AND deleted_at IS NULL
              </Badge>
            </CardBody>
          </Card>

          <Card className="border-light">
            <CardBody>
              <h6 className="text-warning mb-2">⚠️ Key Considerations</h6>
              <ul className="mb-0">
                <li>Closed doesn't always mean successful completion</li>
                <li>Cancellation reasons should be analyzed separately</li>
                <li>Closure date vs. completion date may differ</li>
                <li>Some closed RFQs may be reopened in special cases</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'rework_rfqs':
      return (
        <div>
          <h5 className="text-secondary mb-3">
            <i className="bx bx-refresh me-2"></i>Rework RFQs - Business Logic & Calculation
          </h5>

          <Card className="border-secondary mb-3">
            <CardBody>
              <h6 className="text-secondary mb-3">📊 Business Purpose</h6>
              <p className="mb-3">
                The <strong>Rework RFQs</strong> metric identifies RFQ requests that require revision, correction,
                or additional work, helping track quality issues and process improvement opportunities.
              </p>

              <h6 className="text-primary mb-2">🔢 Calculation Logic</h6>
              <ul className="mb-3">
                <li><strong>Rework Statuses:</strong> 'Rework Required', 'Revision Needed', 'Quality Issues'</li>
                <li><strong>Trigger Events:</strong> Client feedback, internal review, vendor clarifications</li>
                <li><strong>Iteration Count:</strong> May track number of rework cycles</li>
                <li><strong>Reason Codes:</strong> Categorizes types of rework needed</li>
                <li><strong>Timeline Tracking:</strong> Monitors rework duration impact</li>
              </ul>

              <h6 className="text-warning mb-2">💼 Business Impact</h6>
              <ul className="mb-3">
                <li><strong>Quality Control:</strong> Identifies process quality issues</li>
                <li><strong>Cost Analysis:</strong> Tracks additional effort and resources</li>
                <li><strong>Process Improvement:</strong> Highlights areas needing enhancement</li>
                <li><strong>Training Needs:</strong> Identifies skill gaps in teams</li>
              </ul>

              <Badge color="secondary" className="badge-soft mb-2">
                SQL: SELECT COUNT(*) WHERE current_status_id IN (rework_status_ids) AND deleted_at IS NULL
              </Badge>
            </CardBody>
          </Card>

          <Card className="border-light">
            <CardBody>
              <h6 className="text-danger mb-2">🚨 Quality Indicators</h6>
              <ul className="mb-0">
                <li>High rework rate may indicate process issues</li>
                <li>Rework reasons should be categorized and analyzed</li>
                <li>Multiple rework cycles suggest deeper problems</li>
                <li>Rework impacts delivery timelines and costs</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'vendor_invited_rfqs':
      return (
        <div>
          <h5 className="text-primary mb-3">
            <i className="bx bx-user-plus me-2"></i>Vendor Invited RFQs - Business Logic & Calculation
          </h5>

          <Card className="border-primary mb-3">
            <CardBody>
              <h6 className="text-primary mb-3">📊 Business Purpose</h6>
              <p className="mb-3">
                The <strong>Vendor Invited RFQs</strong> metric tracks RFQ requests where vendors have been
                formally invited to participate, measuring vendor outreach effectiveness and engagement levels.
              </p>

              <h6 className="text-success mb-2">🔢 Calculation Logic</h6>
              <ul className="mb-3">
                <li><strong>Invitation Flag:</strong> <code>vendor_invitation_sent = 1</code> or similar</li>
                <li><strong>Invitation Date:</strong> Tracks when invitations were sent</li>
                <li><strong>Multiple Vendors:</strong> May count RFQs with any vendor invited</li>
                <li><strong>Invitation Status:</strong> Sent, delivered, opened, responded</li>
                <li><strong>Vendor Categories:</strong> May filter by vendor type or tier</li>
              </ul>

              <h6 className="text-info mb-2">💼 Business Impact</h6>
              <ul className="mb-3">
                <li><strong>Vendor Engagement:</strong> Measures outreach effectiveness</li>
                <li><strong>Market Coverage:</strong> Tracks vendor market penetration</li>
                <li><strong>Response Rates:</strong> Foundation for vendor response analytics</li>
                <li><strong>Competitive Bidding:</strong> Ensures adequate vendor participation</li>
              </ul>

              <Badge color="primary" className="badge-soft mb-2">
                SQL: SELECT COUNT(DISTINCT rfq_id) WHERE vendor_invitation_sent = 1 AND deleted_at IS NULL
              </Badge>
            </CardBody>
          </Card>

          <Card className="border-light">
            <CardBody>
              <h6 className="text-info mb-2">📈 Optimization Opportunities</h6>
              <ul className="mb-0">
                <li>Low invitation rates may limit competitive bidding</li>
                <li>Track vendor response rates to invitations</li>
                <li>Monitor invitation-to-response conversion</li>
                <li>Analyze vendor category participation patterns</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'work_in_progress_rfqs':
      return (
        <div>
          <h5 className="text-dark mb-3">
            <i className="bx bx-cog me-2"></i>Work in Progress RFQs - Business Logic & Calculation
          </h5>

          <Card className="border-dark mb-3">
            <CardBody>
              <h6 className="text-dark mb-3">📊 Business Purpose</h6>
              <p className="mb-3">
                The <strong>Work in Progress RFQs</strong> metric tracks RFQ requests that are currently being
                actively worked on, providing real-time visibility into current workload and resource utilization.
              </p>

              <h6 className="text-primary mb-2">🔢 Calculation Logic</h6>
              <ul className="mb-3">
                <li><strong>WIP Statuses:</strong> 'In Progress', 'Under Development', 'Being Processed'</li>
                <li><strong>Active Work:</strong> RFQs with recent activity or updates</li>
                <li><strong>Assignment Check:</strong> Must have assigned team member or vendor</li>
                <li><strong>Timeline Tracking:</strong> Monitors duration in WIP status</li>
                <li><strong>Priority Levels:</strong> May include priority-based filtering</li>
              </ul>

              <h6 className="text-success mb-2">💼 Business Impact</h6>
              <ul className="mb-3">
                <li><strong>Workload Management:</strong> Real-time view of active work</li>
                <li><strong>Capacity Planning:</strong> Helps balance team workload</li>
                <li><strong>Bottleneck Detection:</strong> Identifies process slowdowns</li>
                <li><strong>Resource Allocation:</strong> Guides staffing decisions</li>
              </ul>

              <Badge color="dark" className="badge-soft mb-2">
                SQL: SELECT COUNT(*) WHERE current_status_id IN (wip_status_ids) AND assigned_to IS NOT NULL
              </Badge>
            </CardBody>
          </Card>

          <Card className="border-warning">
            <CardBody>
              <h6 className="text-warning mb-2">⚡ Performance Metrics</h6>
              <ul className="mb-3">
                <li><strong>WIP Limits:</strong> Should align with team capacity</li>
                <li><strong>Cycle Time:</strong> Average time RFQs spend in WIP</li>
                <li><strong>Throughput:</strong> Rate of WIP completion</li>
                <li><strong>Age Analysis:</strong> Identify long-running WIP items</li>
              </ul>

              <h6 className="text-danger mb-2">🚨 Warning Indicators</h6>
              <ul className="mb-0">
                <li>High WIP count may indicate capacity issues</li>
                <li>Long WIP duration suggests process problems</li>
                <li>Unassigned WIP items need immediate attention</li>
                <li>WIP without recent activity may be stalled</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'payment_summary_chart':
      return (
        <div>
          <h5 className="text-primary mb-3">
            <i className="bx bx-credit-card me-2"></i>Payment Summary Chart - Business Logic & Calculation
          </h5>

          <Card className="border-primary mb-3">
            <CardBody>
              <h6 className="text-primary mb-3">📊 Business Purpose</h6>
              <p className="mb-3">
                The <strong>Payment Summary Chart</strong> provides a visual breakdown of payment statuses across all RFQ milestones,
                helping track cash flow, payment processing efficiency, and outstanding payment obligations.
              </p>

              <h6 className="text-success mb-2">🔢 Calculation Logic</h6>
              <ul className="mb-3">
                <li><strong>Data Source:</strong> <code>tbl_rfq_task_payment_milestone</code> + <code>rfq_payment</code> tables</li>
                <li><strong>Grouping:</strong> Groups payments by status (Paid, Pending, Overdue, Requested)</li>
                <li><strong>Aggregation:</strong> SUM(payment_amount) for each status category</li>
                <li><strong>Status Logic:</strong> Based on payment.status field and due dates</li>
                <li><strong>Currency:</strong> All amounts converted to Indian Rupees (₹)</li>
              </ul>

              <h6 className="text-info mb-2">💼 Business Impact</h6>
              <ul className="mb-3">
                <li><strong>Cash Flow Management:</strong> Tracks pending vs. completed payments</li>
                <li><strong>Vendor Relations:</strong> Monitors payment processing efficiency</li>
                <li><strong>Financial Planning:</strong> Identifies upcoming payment obligations</li>
                <li><strong>Process Optimization:</strong> Highlights payment bottlenecks</li>
              </ul>

              <Badge color="primary" className="badge-soft mb-2">
                SQL: SELECT payment_status, SUM(payment_amount) FROM milestone_payments GROUP BY status
              </Badge>
            </CardBody>
          </Card>

          <Card className="border-light">
            <CardBody>
              <h6 className="text-warning mb-2">📈 Chart Interactions</h6>
              <ul className="mb-3">
                <li><strong>Click Bars:</strong> View detailed payment records for each status</li>
                <li><strong>Hover Tooltips:</strong> Show exact amounts and percentages</li>
                <li><strong>Real-time Updates:</strong> Chart refreshes with payment status changes</li>
                <li><strong>Drill-down:</strong> Access individual payment details</li>
              </ul>

              <h6 className="text-info mb-2">🎯 Key Metrics</h6>
              <ul className="mb-0">
                <li><strong>Paid:</strong> Successfully processed payments</li>
                <li><strong>Pending:</strong> Approved but not yet processed</li>
                <li><strong>Overdue:</strong> Past due date payments</li>
                <li><strong>Requested:</strong> Submitted for approval</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'status_distribution_chart':
      return (
        <div>
          <h5 className="text-info mb-3">
            <i className="bx bx-bar-chart-alt me-2"></i>Status Distribution Chart - Business Logic & Calculation
          </h5>

          <Card className="border-info mb-3">
            <CardBody>
              <h6 className="text-info mb-3">📊 Business Purpose</h6>
              <p className="mb-3">
                The <strong>Status Distribution Chart</strong> visualizes the distribution of RFQs across different status categories,
                providing insights into workflow bottlenecks, process efficiency, and overall RFQ lifecycle management.
              </p>

              <h6 className="text-primary mb-2">🔢 Calculation Logic</h6>
              <ul className="mb-3">
                <li><strong>Data Source:</strong> <code>request_for_quotation_management</code> + <code>request_for_quotation_status_mst</code></li>
                <li><strong>Join Logic:</strong> Links RFQs with their current status definitions</li>
                <li><strong>Grouping:</strong> Groups by status_name and counts occurrences</li>
                <li><strong>Aggregation:</strong> COUNT(*) for each status + SUM(budget_amount)</li>
                <li><strong>Filtering:</strong> Applies date range and project filters</li>
              </ul>

              <h6 className="text-success mb-2">💼 Business Impact</h6>
              <ul className="mb-3">
                <li><strong>Workflow Analysis:</strong> Identifies where RFQs accumulate</li>
                <li><strong>Bottleneck Detection:</strong> Highlights process slowdowns</li>
                <li><strong>Resource Planning:</strong> Shows workload distribution</li>
                <li><strong>Process Optimization:</strong> Guides workflow improvements</li>
              </ul>

              <Badge color="info" className="badge-soft mb-2">
                SQL: SELECT status_name, COUNT(*), SUM(budget) FROM rfq JOIN status ON current_status_id = status.id GROUP BY status_name
              </Badge>
            </CardBody>
          </Card>

          <Card className="border-light">
            <CardBody>
              <h6 className="text-warning mb-2">📈 Chart Features</h6>
              <ul className="mb-3">
                <li><strong>Interactive Bars:</strong> Click to view RFQs in each status</li>
                <li><strong>Hover Details:</strong> Shows count, percentage, and total value</li>
                <li><strong>Color Coding:</strong> Status-based color scheme for easy identification</li>
                <li><strong>Real-time Updates:</strong> Refreshes with status changes</li>
              </ul>

              <h6 className="text-info mb-2">🎯 Status Categories</h6>
              <ul className="mb-0">
                <li><strong>Open:</strong> Newly created, awaiting processing</li>
                <li><strong>In Progress:</strong> Currently being worked on</li>
                <li><strong>Under Review:</strong> Pending approval or evaluation</li>
                <li><strong>Completed:</strong> Successfully finished</li>
                <li><strong>Cancelled:</strong> Terminated or rejected</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'status_analytics_total':
      return (
        <div>
          <h5 className="text-primary mb-3">
            <i className="bx bx-file-list-3-line me-2"></i>Total RFQ (Status Analytics) - Business Logic & Calculation
          </h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">📊 Data Source & Calculation</h6>
              <p className="mb-2"><strong>Primary Data Source:</strong></p>
              <ul className="mb-2">
                <li><strong>Main Table:</strong> <code>tbl_rfq</code></li>
                <li><strong>API Endpoint:</strong> <code>/api/rfq/by-status/total</code></li>
                <li><strong>Calculation:</strong> COUNT(*) of all RFQ records</li>
                <li><strong>Filters Applied:</strong> Date range, department, project filters</li>
              </ul>

              <h6 className="text-info mb-2">🔍 Business Logic</h6>
              <ul className="mb-2">
                <li>Counts all RFQ records regardless of status</li>
                <li>Includes active, completed, cancelled, and archived RFQs</li>
                <li>Provides baseline metric for all other status calculations</li>
                <li>Used for percentage calculations in status distribution</li>
              </ul>

              <h6 className="text-warning mb-2">⚠️ Key Considerations</h6>
              <ul className="mb-0">
                <li>Total should equal sum of all individual status counts</li>
                <li>Soft-deleted RFQs may or may not be included based on API logic</li>
                <li>Real-time updates as new RFQs are created</li>
                <li>Click card to view detailed RFQ list</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'status_analytics_created':
      return (
        <div>
          <h5 className="text-success mb-3">
            <i className="bx bx-add-circle-line me-2"></i>Created RFQ (Status Analytics) - Business Logic & Calculation
          </h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">📊 Data Source & Calculation</h6>
              <p className="mb-2"><strong>Primary Data Source:</strong></p>
              <ul className="mb-2">
                <li><strong>Main Table:</strong> <code>tbl_rfq</code></li>
                <li><strong>API Endpoint:</strong> <code>/api/rfq/by-status/created</code></li>
                <li><strong>Status Filter:</strong> WHERE status = 'Created' OR status = 'Draft'</li>
                <li><strong>Calculation:</strong> COUNT(*) of matching records</li>
              </ul>

              <h6 className="text-info mb-2">🔍 Business Logic</h6>
              <ul className="mb-2">
                <li>RFQs that have been initiated but not yet assigned or started</li>
                <li>Includes both 'Created' and 'Draft' status RFQs</li>
                <li>Represents the pipeline of new work requests</li>
                <li>Key metric for workload planning and resource allocation</li>
              </ul>

              <h6 className="text-warning mb-2">⚠️ Key Considerations</h6>
              <ul className="mb-0">
                <li>High numbers may indicate bottlenecks in assignment process</li>
                <li>Should be regularly reviewed for priority assignments</li>
                <li>Age of created RFQs is important for SLA compliance</li>
                <li>Click card to view and manage created RFQs</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'status_analytics_wip':
      return (
        <div>
          <h5 className="text-warning mb-3">
            <i className="bx bx-time-line me-2"></i>Work in Progress RFQ (Status Analytics) - Business Logic & Calculation
          </h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">📊 Data Source & Calculation</h6>
              <p className="mb-2"><strong>Primary Data Source:</strong></p>
              <ul className="mb-2">
                <li><strong>Main Table:</strong> <code>tbl_rfq</code></li>
                <li><strong>API Endpoint:</strong> <code>/api/rfq/by-status/wip</code></li>
                <li><strong>Status Filter:</strong> WHERE status IN ('In Progress', 'Assigned', 'Under Review')</li>
                <li><strong>Calculation:</strong> COUNT(*) of active work items</li>
              </ul>

              <h6 className="text-info mb-2">🔍 Business Logic</h6>
              <ul className="mb-2">
                <li>RFQs currently being worked on by teams or vendors</li>
                <li>Includes assigned, in-progress, and under-review statuses</li>
                <li>Represents current active workload</li>
                <li>Critical for capacity planning and resource management</li>
              </ul>

              <h6 className="text-warning mb-2">⚠️ Key Considerations</h6>
              <ul className="mb-0">
                <li>High WIP may indicate resource constraints or bottlenecks</li>
                <li>Should be balanced with team capacity</li>
                <li>Long-running WIP items need attention for completion</li>
                <li>Click card to monitor progress and identify blockers</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'status_analytics_closed':
      return (
        <div>
          <h5 className="text-info mb-3">
            <i className="bx bx-check-double-line me-2"></i>Closed RFQ (Status Analytics) - Business Logic & Calculation
          </h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">📊 Data Source & Calculation</h6>
              <p className="mb-2"><strong>Primary Data Source:</strong></p>
              <ul className="mb-2">
                <li><strong>Main Table:</strong> <code>tbl_rfq</code></li>
                <li><strong>API Endpoint:</strong> <code>/api/rfq/by-status/closed</code></li>
                <li><strong>Status Filter:</strong> WHERE status IN ('Completed', 'Cancelled', 'Closed')</li>
                <li><strong>Calculation:</strong> COUNT(*) of finalized records</li>
              </ul>

              <h6 className="text-info mb-2">🔍 Business Logic</h6>
              <ul className="mb-2">
                <li>RFQs that have reached final status (completed or cancelled)</li>
                <li>Includes successful completions and cancellations</li>
                <li>Represents delivered value and completed work</li>
                <li>Key metric for productivity and completion rates</li>
              </ul>

              <h6 className="text-warning mb-2">⚠️ Key Considerations</h6>
              <ul className="mb-0">
                <li>Distinguish between completed vs cancelled for success metrics</li>
                <li>Closure date important for cycle time analysis</li>
                <li>Closed RFQs contribute to historical performance data</li>
                <li>Click card to analyze completion patterns and outcomes</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'financial_insights_total_budget':
      return (
        <div>
          <h5 className="text-primary mb-3">
            <i className="bx bx-money me-2"></i>Total Budget (Financial Insights) - Business Logic & Calculation
          </h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">📊 Data Source & Calculation</h6>
              <p className="mb-2"><strong>Primary Data Source:</strong></p>
              <ul className="mb-2">
                <li><strong>Main Table:</strong> <code>tbl_rfq</code></li>
                <li><strong>Field:</strong> <code>budget_amount</code> or <code>estimated_budget</code></li>
                <li><strong>Calculation:</strong> SUM(budget_amount) for all RFQs in scope</li>
                <li><strong>Filters Applied:</strong> Date range, department, project, status filters</li>
              </ul>

              <h6 className="text-info mb-2">🔍 Business Logic</h6>
              <ul className="mb-2">
                <li>Represents total allocated budget for all RFQs in the selected period</li>
                <li>Includes both active and completed RFQ budgets</li>
                <li>Used as baseline for budget utilization calculations</li>
                <li>Critical for financial planning and resource allocation</li>
              </ul>

              <h6 className="text-warning mb-2">⚠️ Key Considerations</h6>
              <ul className="mb-0">
                <li>Budget vs actual spend variance should be monitored</li>
                <li>Includes initial estimates that may change during execution</li>
                <li>Budget revisions and amendments should be tracked separately</li>
                <li>Click card to view detailed budget breakdown by RFQ</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'financial_insights_total_paid':
      return (
        <div>
          <h5 className="text-success mb-3">
            <i className="bx bx-credit-card me-2"></i>Total Paid (Financial Insights) - Business Logic & Calculation
          </h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">📊 Data Source & Calculation</h6>
              <p className="mb-2"><strong>Primary Data Source:</strong></p>
              <ul className="mb-2">
                <li><strong>Main Tables:</strong> <code>tbl_rfq_task_payment_milestone</code> + <code>rfq_payment</code></li>
                <li><strong>Relationship:</strong> milestone.id = payment.milestone_id</li>
                <li><strong>Calculation:</strong> SUM(CASE WHEN payment.status = "paid" THEN payment.paid_amount ELSE 0 END)</li>
                <li><strong>Status Filter:</strong> WHERE payment.status = 'paid' OR payment.status = 'completed'</li>
              </ul>

              <h6 className="text-info mb-2">🔍 Business Logic</h6>
              <ul className="mb-2">
                <li>Actual money disbursed to vendors and contractors</li>
                <li>Includes milestone payments, advance payments, and final settlements</li>
                <li>Represents cash outflow and completed financial obligations</li>
                <li>Key metric for cash flow management and payment tracking</li>
              </ul>

              <h6 className="text-warning mb-2">⚠️ Key Considerations</h6>
              <ul className="mb-0">
                <li>Payment date vs milestone completion date may differ</li>
                <li>Includes partial payments and installments</li>
                <li>Should reconcile with accounting system records</li>
                <li>Click card to view payment history and transaction details</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'financial_insights_pending_amount':
      return (
        <div>
          <h5 className="text-warning mb-3">
            <i className="bx bx-time-five me-2"></i>Pending Amount (Financial Insights) - Business Logic & Calculation
          </h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">📊 Data Source & Calculation</h6>
              <p className="mb-2"><strong>Primary Data Source:</strong></p>
              <ul className="mb-2">
                <li><strong>Main Tables:</strong> <code>tbl_rfq_task_payment_milestone</code> + <code>rfq_payment</code></li>
                <li><strong>Relationship:</strong> milestone.id = payment.milestone_id</li>
                <li><strong>Calculation:</strong> SUM(CASE WHEN payment.status IN ("pending", "requested", "approved") THEN payment.amount_requested ELSE 0 END)</li>
                <li><strong>Status Filter:</strong> WHERE payment.status IN ('pending', 'requested', 'approved', 'processing')</li>
              </ul>

              <h6 className="text-info mb-2">🔍 Business Logic</h6>
              <ul className="mb-2">
                <li>Outstanding payment obligations awaiting processing</li>
                <li>Includes approved but unpaid milestone payments</li>
                <li>Represents future cash outflow commitments</li>
                <li>Critical for cash flow forecasting and liquidity planning</li>
              </ul>

              <h6 className="text-warning mb-2">⚠️ Key Considerations</h6>
              <ul className="mb-0">
                <li>High pending amounts may indicate cash flow constraints</li>
                <li>Payment terms and due dates should be monitored</li>
                <li>Vendor relationships may be affected by payment delays</li>
                <li>Click card to prioritize and manage pending payments</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'project_analytics_total_projects':
      return (
        <div>
          <h5 className="text-primary mb-3">
            <i className="bx bx-building me-2"></i>Total Projects (Project Analytics) - Business Logic & Calculation
          </h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">📊 Data Source & Calculation</h6>
              <p className="mb-2"><strong>Primary Data Source:</strong></p>
              <ul className="mb-2">
                <li><strong>Main Table:</strong> <code>tbl_project</code></li>
                <li><strong>Relationship:</strong> project.id = rfq.project_id</li>
                <li><strong>Calculation:</strong> COUNT(DISTINCT project.id) WHERE project has associated RFQs</li>
                <li><strong>Filters Applied:</strong> Date range, department, project status filters</li>
              </ul>

              <h6 className="text-info mb-2">🔍 Business Logic</h6>
              <ul className="mb-2">
                <li>Active projects with RFQ activities in the selected period</li>
                <li>Includes projects in planning, execution, and completion phases</li>
                <li>Represents organizational project portfolio scope</li>
                <li>Key metric for resource allocation and capacity planning</li>
              </ul>

              <h6 className="text-warning mb-2">⚠️ Key Considerations</h6>
              <ul className="mb-0">
                <li>Only counts projects with active RFQ engagement</li>
                <li>Project complexity varies significantly across types</li>
                <li>Geographic distribution affects resource requirements</li>
                <li>Click card to view detailed project breakdown and analytics</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'project_analytics_total_rfqs':
      return (
        <div>
          <h5 className="text-success mb-3">
            <i className="bx bx-list-ul me-2"></i>Total RFQs (Project Analytics) - Business Logic & Calculation
          </h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">📊 Data Source & Calculation</h6>
              <p className="mb-2"><strong>Primary Data Source:</strong></p>
              <ul className="mb-2">
                <li><strong>Main Table:</strong> <code>tbl_rfq</code></li>
                <li><strong>Relationship:</strong> rfq.project_id = project.id</li>
                <li><strong>Calculation:</strong> COUNT(rfq.id) grouped by project</li>
                <li><strong>Status Filter:</strong> All RFQ statuses included (draft, active, completed, cancelled)</li>
              </ul>

              <h6 className="text-info mb-2">🔍 Business Logic</h6>
              <ul className="mb-2">
                <li>Total procurement activities across all projects</li>
                <li>Includes RFQs in all lifecycle stages</li>
                <li>Represents procurement workload and complexity</li>
                <li>Key indicator of project activity and vendor engagement</li>
              </ul>

              <h6 className="text-warning mb-2">⚠️ Key Considerations</h6>
              <ul className="mb-0">
                <li>RFQ count doesn't reflect monetary value or complexity</li>
                <li>Some projects may have higher RFQ density than others</li>
                <li>Assignment rates vary by project type and urgency</li>
                <li>Click card to analyze RFQ distribution and assignment patterns</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'project_analytics_total_budget':
      return (
        <div>
          <h5 className="text-info mb-3">
            <i className="bx bx-money me-2"></i>Total Budget (Project Analytics) - Business Logic & Calculation
          </h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">📊 Data Source & Calculation</h6>
              <p className="mb-2"><strong>Primary Data Source:</strong></p>
              <ul className="mb-2">
                <li><strong>Main Tables:</strong> <code>tbl_rfq</code> + <code>tbl_project</code></li>
                <li><strong>Relationship:</strong> rfq.project_id = project.id</li>
                <li><strong>Calculation:</strong> SUM(rfq.budget_amount) grouped by project, then summed across projects</li>
                <li><strong>Aggregation:</strong> Project-level budget totals aggregated to portfolio level</li>
              </ul>

              <h6 className="text-info mb-2">🔍 Business Logic</h6>
              <ul className="mb-2">
                <li>Total allocated budget across all active projects</li>
                <li>Represents organizational financial commitment</li>
                <li>Used for portfolio-level budget planning and control</li>
                <li>Critical for cash flow forecasting and resource allocation</li>
              </ul>

              <h6 className="text-warning mb-2">⚠️ Key Considerations</h6>
              <ul className="mb-0">
                <li>Budget allocations may change during project execution</li>
                <li>Different projects have varying budget utilization patterns</li>
                <li>Budget vs actual variance should be monitored at project level</li>
                <li>Click card to analyze budget distribution and utilization by project</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'project_analytics_total_paid':
      return (
        <div>
          <h5 className="text-warning mb-3">
            <i className="bx bx-credit-card me-2"></i>Total Paid (Project Analytics) - Business Logic & Calculation
          </h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">📊 Data Source & Calculation</h6>
              <p className="mb-2"><strong>Primary Data Source:</strong></p>
              <ul className="mb-2">
                <li><strong>Main Tables:</strong> <code>rfq_payment</code> + <code>tbl_rfq_task_payment_milestone</code> + <code>tbl_rfq</code></li>
                <li><strong>Relationship:</strong> payment.milestone_id = milestone.id, milestone.rfq_id = rfq.id, rfq.project_id = project.id</li>
                <li><strong>Calculation:</strong> SUM(payment.paid_amount) WHERE payment.status = 'paid' grouped by project</li>
                <li><strong>Aggregation:</strong> Project-level payment totals summed across portfolio</li>
              </ul>

              <h6 className="text-info mb-2">🔍 Business Logic</h6>
              <ul className="mb-2">
                <li>Actual cash disbursements across all projects</li>
                <li>Represents completed financial obligations</li>
                <li>Key metric for cash flow analysis and payment tracking</li>
                <li>Used to assess project payment completion rates</li>
              </ul>

              <h6 className="text-warning mb-2">⚠️ Key Considerations</h6>
              <ul className="mb-0">
                <li>Payment timing may lag behind milestone completion</li>
                <li>Different projects have varying payment schedules</li>
                <li>Payment completion rates indicate project financial health</li>
                <li>Click card to analyze payment patterns and completion rates by project</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'timeline_analytics_total_rfqs':
      return (
        <div>
          <h5 className="text-primary mb-3">
            <i className="bx bx-time me-2"></i>Total RFQs (Timeline Analytics) - Business Logic & Calculation
          </h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">📊 Data Source & Calculation</h6>
              <p className="mb-2"><strong>Primary Data Source:</strong></p>
              <ul className="mb-2">
                <li><strong>Main Table:</strong> <code>tbl_rfq</code></li>
                <li><strong>Time Dimension:</strong> rfq.created_at, rfq.updated_at, milestone.due_date</li>
                <li><strong>Calculation:</strong> COUNT(rfq.id) within selected date range</li>
                <li><strong>Timeline Grouping:</strong> Daily, Weekly, Monthly, or Yearly aggregation</li>
              </ul>

              <h6 className="text-info mb-2">🔍 Business Logic</h6>
              <ul className="mb-2">
                <li>RFQs with active timeline activities in the selected period</li>
                <li>Includes RFQs with milestone deadlines, task updates, or status changes</li>
                <li>Represents procurement activity volume over time</li>
                <li>Key metric for workload planning and resource allocation</li>
              </ul>

              <h6 className="text-warning mb-2">⚠️ Key Considerations</h6>
              <ul className="mb-0">
                <li>Timeline view focuses on time-sensitive RFQ activities</li>
                <li>Seasonal patterns may affect RFQ volume distribution</li>
                <li>Milestone clustering can create timeline bottlenecks</li>
                <li>Click card to analyze RFQ timeline distribution and activity patterns</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'timeline_analytics_active_milestones':
      return (
        <div>
          <h5 className="text-success mb-3">
            <i className="bx bx-target-lock me-2"></i>Active Milestones - Business Logic & Calculation
          </h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">📊 Data Source & Calculation</h6>
              <p className="mb-2"><strong>Primary Data Source:</strong></p>
              <ul className="mb-2">
                <li><strong>Main Table:</strong> <code>tbl_rfq_task_payment_milestone</code></li>
                <li><strong>Relationship:</strong> milestone.rfq_id = rfq.id</li>
                <li><strong>Calculation:</strong> COUNT(milestone.id) WHERE milestone.status IN ('pending', 'in_progress', 'review')</li>
                <li><strong>Time Filter:</strong> milestone.due_date within timeline period or status = active</li>
              </ul>

              <h6 className="text-info mb-2">🔍 Business Logic</h6>
              <ul className="mb-2">
                <li>Milestones requiring attention or action within the timeline</li>
                <li>Includes pending approvals, in-progress work, and upcoming deadlines</li>
                <li>Represents active workload and delivery commitments</li>
                <li>Critical for timeline management and deadline tracking</li>
              </ul>

              <h6 className="text-warning mb-2">⚠️ Key Considerations</h6>
              <ul className="mb-0">
                <li>High active milestone count may indicate resource constraints</li>
                <li>Milestone dependencies can create timeline cascades</li>
                <li>Due date clustering requires careful resource planning</li>
                <li>Click card to prioritize milestones and manage timeline risks</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'timeline_analytics_active_tasks':
      return (
        <div>
          <h5 className="text-info mb-3">
            <i className="bx bx-task me-2"></i>Active Tasks - Business Logic & Calculation
          </h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">📊 Data Source & Calculation</h6>
              <p className="mb-2"><strong>Primary Data Source:</strong></p>
              <ul className="mb-2">
                <li><strong>Main Tables:</strong> <code>tbl_rfq_task</code> + <code>tbl_rfq_task_payment_milestone</code></li>
                <li><strong>Relationship:</strong> task.milestone_id = milestone.id</li>
                <li><strong>Calculation:</strong> COUNT(task.id) WHERE task.status IN ('assigned', 'in_progress', 'review')</li>
                <li><strong>Progress Tracking:</strong> task.progress_percentage &lt; 100 OR task.status ≠ 'completed'</li>
              </ul>

              <h6 className="text-info mb-2">🔍 Business Logic</h6>
              <ul className="mb-2">
                <li>Tasks currently being worked on or awaiting action</li>
                <li>Includes assigned tasks, work in progress, and pending reviews</li>
                <li>Represents immediate workload and resource utilization</li>
                <li>Key indicator of team productivity and task completion velocity</li>
              </ul>

              <h6 className="text-warning mb-2">⚠️ Key Considerations</h6>
              <ul className="mb-0">
                <li>Task complexity varies significantly across RFQ types</li>
                <li>Resource availability affects task completion timelines</li>
                <li>Task dependencies may create workflow bottlenecks</li>
                <li>Click card to monitor task progress and optimize resource allocation</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'recent_activities':
      return (
        <div>
          <h5 className="text-success mb-3">
            <i className="bx bx-time me-2"></i>Recent Activities - Business Logic & Data Source
          </h5>

          <Card className="border-success mb-3">
            <CardBody>
              <h6 className="text-success mb-3">📊 Business Purpose</h6>
              <p className="mb-3">
                The <strong>Recent Activities</strong> section provides a real-time feed of the latest actions and updates
                across all RFQ processes, helping teams stay informed about current developments and system changes.
              </p>

              <h6 className="text-primary mb-2">🔢 Data Source & Logic</h6>
              <ul className="mb-3">
                <li><strong>Activity Tables:</strong> Multiple tables including status changes, assignments, payments</li>
                <li><strong>Event Types:</strong> Status updates, vendor assignments, milestone completions, payments</li>
                <li><strong>Timestamp Ordering:</strong> ORDER BY created_at DESC (newest first)</li>
                <li><strong>Record Limit:</strong> Limited to last 50 activities for performance</li>
                <li><strong>User Tracking:</strong> Links activities to responsible users</li>
              </ul>

              <h6 className="text-info mb-2">💼 Business Impact</h6>
              <ul className="mb-3">
                <li><strong>Real-time Monitoring:</strong> Stay updated on latest changes</li>
                <li><strong>Team Coordination:</strong> Track what colleagues are working on</li>
                <li><strong>Audit Trail:</strong> Maintain history of important actions</li>
                <li><strong>Quick Access:</strong> Jump to recently modified RFQs</li>
              </ul>

              <Badge color="success" className="badge-soft mb-2">
                SQL: SELECT * FROM activity_log ORDER BY created_at DESC LIMIT 50
              </Badge>
            </CardBody>
          </Card>

          <Card className="border-light">
            <CardBody>
              <h6 className="text-warning mb-2">📋 Activity Types</h6>
              <ul className="mb-3">
                <li><strong>Status Changes:</strong> RFQ status transitions and updates</li>
                <li><strong>Vendor Actions:</strong> Assignments, bid submissions, responses</li>
                <li><strong>Payment Events:</strong> Milestone payments, approvals, completions</li>
                <li><strong>Document Updates:</strong> File uploads, document modifications</li>
                <li><strong>System Events:</strong> Automated notifications and reminders</li>
              </ul>

              <h6 className="text-info mb-2">🔄 Real-time Features</h6>
              <ul className="mb-0">
                <li><strong>Auto-refresh:</strong> Updates every few minutes</li>
                <li><strong>User Context:</strong> Shows who performed each action</li>
                <li><strong>Clickable Links:</strong> Direct navigation to related RFQs</li>
                <li><strong>Time Stamps:</strong> Relative time display (e.g., "2 hours ago")</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'status_analytics_additional_milestones':
      return (
        <div>
          <h5 className="text-primary mb-3">
            <i className="bx bx-task me-2"></i>Additional Milestones - Business Logic & Calculation
          </h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">📊 Data Source & Calculation</h6>
              <p className="mb-2"><strong>Primary Data Source:</strong></p>
              <ul className="mb-2">
                <li><strong>Table:</strong> additional_milestones (Additional Milestones table)</li>
                <li><strong>Join:</strong> rfq_mst (RFQ Master table)</li>
                <li><strong>Filter:</strong> Active milestones only (deleted_at IS NULL)</li>
                <li><strong>User Context:</strong> Based on user's project access permissions</li>
              </ul>

              <p className="mb-2"><strong>Calculation Logic:</strong></p>
              <ul className="mb-2">
                <li><strong>Total Count:</strong> COUNT(*) of additional milestones</li>
                <li><strong>Total Payment:</strong> SUM(payment_amount) across all milestones</li>
                <li><strong>Status Tracking:</strong> Groups by milestone status</li>
                <li><strong>RFQ Association:</strong> Links milestones to parent RFQs</li>
              </ul>

              <Badge color="purple" className="badge-soft">
                SQL: SELECT COUNT(*) as total_milestones, SUM(payment_amount) as total_payment FROM additional_milestones WHERE deleted_at IS NULL
              </Badge>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-success mb-2">💼 Business Impact</h6>
              <ul className="mb-3">
                <li><strong>Project Extensions:</strong> Tracks additional work beyond original scope</li>
                <li><strong>Revenue Tracking:</strong> Monitors extra income from milestone additions</li>
                <li><strong>Scope Management:</strong> Helps control project scope creep</li>
                <li><strong>Client Satisfaction:</strong> Shows flexibility in accommodating client needs</li>
              </ul>

              <h6 className="text-info mb-2">🎯 Key Insights</h6>
              <ul className="mb-0">
                <li>Higher counts may indicate scope expansion trends</li>
                <li>Payment amounts show additional revenue potential</li>
                <li>Click to view detailed milestone breakdown</li>
                <li>Helps in future project planning and estimation</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'status_analytics_additional_tasks':
      return (
        <div>
          <h5 className="text-primary mb-3">
            <i className="bx bx-list-check me-2"></i>Additional Tasks - Business Logic & Calculation
          </h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">📊 Data Source & Calculation</h6>
              <p className="mb-2"><strong>Primary Data Source:</strong></p>
              <ul className="mb-2">
                <li><strong>Table:</strong> additional_tasks (Additional Tasks table)</li>
                <li><strong>Join:</strong> rfq_mst (RFQ Master table)</li>
                <li><strong>Filter:</strong> Active tasks only (deleted_at IS NULL)</li>
                <li><strong>User Context:</strong> Based on user's project access permissions</li>
              </ul>

              <p className="mb-2"><strong>Calculation Logic:</strong></p>
              <ul className="mb-2">
                <li><strong>Total Count:</strong> COUNT(*) of additional tasks</li>
                <li><strong>Total Payment:</strong> SUM(payment_amount) across all tasks</li>
                <li><strong>Status Distribution:</strong> Groups by task completion status</li>
                <li><strong>Priority Analysis:</strong> Categorizes by task priority levels</li>
              </ul>

              <Badge color="warning" className="badge-soft">
                SQL: SELECT COUNT(*) as total_tasks, SUM(payment_amount) as total_payment FROM additional_tasks WHERE deleted_at IS NULL
              </Badge>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-warning mb-2">⚡ Task Management</h6>
              <ul className="mb-3">
                <li><strong>Workload Tracking:</strong> Monitors additional work assignments</li>
                <li><strong>Resource Planning:</strong> Helps allocate resources for extra tasks</li>
                <li><strong>Cost Management:</strong> Tracks additional costs from task additions</li>
                <li><strong>Delivery Timeline:</strong> Impact on project completion schedules</li>
              </ul>

              <h6 className="text-info mb-2">🎯 Performance Indicators</h6>
              <ul className="mb-0">
                <li>Task count indicates project complexity growth</li>
                <li>Payment amounts show additional revenue streams</li>
                <li>Status distribution reveals task completion efficiency</li>
                <li>Click to view detailed task breakdown and priorities</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'financial_insights_total_final_amount':
      return (
        <div>
          <h5 className="text-primary mb-3">
            <i className="bx bx-receipt me-2"></i>Total Final Amount - Business Logic & Calculation
          </h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">📊 Data Source & Calculation</h6>
              <p className="mb-2"><strong>Primary Data Source:</strong></p>
              <ul className="mb-2">
                <li><strong>Table:</strong> rfq_mst (RFQ Master table)</li>
                <li><strong>Additional Tables:</strong> additional_milestones, additional_tasks</li>
                <li><strong>Filter:</strong> Active RFQs only (deleted_at IS NULL)</li>
                <li><strong>User Context:</strong> Based on user's project access permissions</li>
              </ul>

              <p className="mb-2"><strong>Calculation Logic:</strong></p>
              <ul className="mb-2">
                <li><strong>Base Final Amount:</strong> SUM(final_amount) from rfq_mst</li>
                <li><strong>Additional Milestones:</strong> SUM(payment_amount) from additional_milestones</li>
                <li><strong>Additional Tasks:</strong> SUM(payment_amount) from additional_tasks</li>
                <li><strong>Total Final Amount:</strong> Base + Milestones + Tasks</li>
              </ul>

              <Badge color="info" className="badge-soft">
                SQL: SELECT SUM(rfq_mst.final_amount + COALESCE(milestones.total, 0) + COALESCE(tasks.total, 0)) as total_final_amount
              </Badge>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-success mb-2">💰 Financial Composition</h6>
              <ul className="mb-3">
                <li><strong>Core RFQ Value:</strong> Original contracted amounts from RFQs</li>
                <li><strong>Milestone Additions:</strong> Extra payments from additional milestones</li>
                <li><strong>Task Additions:</strong> Extra payments from additional tasks</li>
                <li><strong>Total Project Value:</strong> Complete financial commitment</li>
              </ul>

              <h6 className="text-info mb-2">🎯 Business Insights</h6>
              <ul className="mb-0">
                <li>Represents total revenue potential from all projects</li>
                <li>Includes both original and additional work values</li>
                <li>Key metric for financial planning and forecasting</li>
                <li>Click to view detailed breakdown by RFQ and additions</li>
              </ul>
            </CardBody>
          </Card>

          <Card className="border-warning">
            <CardBody>
              <h6 className="text-warning mb-2">⚠️ Important Notes</h6>
              <ul className="mb-0">
                <li><strong>Scope Expansion:</strong> Higher amounts may indicate scope creep</li>
                <li><strong>Revenue Growth:</strong> Additional work contributes to total revenue</li>
                <li><strong>Budget Comparison:</strong> Compare with total budget to assess profitability</li>
                <li><strong>Payment Tracking:</strong> Use with paid amounts to monitor collections</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'good_service_category':
      return (
        <div>
          <h5 className="text-primary mb-3">
            <i className="bx bx-pie-chart-alt me-2"></i>Category Distribution - Business Logic & Calculation
          </h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">📊 Data Source & Calculation</h6>
              <p className="mb-2"><strong>Primary Data Source:</strong></p>
              <ul className="mb-2">
                <li><strong>Table:</strong> rfq_mst (RFQ Master table)</li>
                <li><strong>Join:</strong> service_heads_mst (Service Heads Master)</li>
                <li><strong>Filter:</strong> Active RFQs only (deleted_at IS NULL)</li>
                <li><strong>User Context:</strong> Based on user's project access permissions</li>
              </ul>

              <p className="mb-2"><strong>Calculation Logic:</strong></p>
              <ul className="mb-2">
                <li>Groups RFQs by service_head_id</li>
                <li>Counts total RFQs per service category</li>
                <li>Calculates percentage distribution</li>
                <li>Orders by RFQ count (descending)</li>
              </ul>

              <div className="bg-light p-2 rounded">
                <small className="text-muted d-block mb-1"><strong>SQL Query:</strong></small>
                <code style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                  SELECT service_heads.name, COUNT(rfq_mst.id) as count<br/>
                  FROM rfq_mst JOIN service_heads_mst<br/>
                  WHERE deleted_at IS NULL<br/>
                  GROUP BY service_head_id
                </code>
              </div>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-success mb-2">💼 Business Impact</h6>
              <ul className="mb-3">
                <li><strong>Service Demand:</strong> Identifies most requested service categories</li>
                <li><strong>Resource Planning:</strong> Helps allocate resources to high-demand services</li>
                <li><strong>Market Analysis:</strong> Shows service portfolio performance</li>
                <li><strong>Strategic Planning:</strong> Guides service expansion decisions</li>
              </ul>

              <h6 className="text-info mb-2">🎯 Key Insights</h6>
              <ul className="mb-0">
                <li>Click on chart segments to view detailed RFQ list</li>
                <li>Percentage shows relative demand for each service</li>
                <li>Helps identify underutilized service categories</li>
                <li>Supports capacity planning and resource allocation</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'good_service_payment':
      return (
        <div>
          <h5 className="text-primary mb-3">
            <i className="bx bx-credit-card me-2"></i>Payment Status Analysis - Business Logic & Calculation
          </h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">📊 Data Source & Calculation</h6>
              <p className="mb-2"><strong>Primary Data Source:</strong></p>
              <ul className="mb-2">
                <li><strong>Table:</strong> rfq_mst (RFQ Master table)</li>
                <li><strong>Join:</strong> service_heads_mst (Service Heads Master)</li>
                <li><strong>Filter:</strong> Active RFQs with final amounts (deleted_at IS NULL, final_amount &gt; 0)</li>
                <li><strong>User Context:</strong> Based on user's project access permissions</li>
              </ul>

              <p className="mb-2"><strong>Calculation Logic:</strong></p>
              <ul className="mb-2">
                <li>Groups by service_head_id</li>
                <li><strong>Total Final Amount:</strong> SUM(final_amount) per service head</li>
                <li><strong>Total Paid Amount:</strong> SUM(paid_amount) per service head</li>
                <li><strong>Payment Percentage:</strong> (paid_amount / final_amount) * 100</li>
                <li><strong>Pending Amount:</strong> final_amount - paid_amount</li>
              </ul>

              <div className="bg-light p-2 rounded">
                <small className="text-muted d-block mb-1"><strong>SQL Query:</strong></small>
                <code style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                  SELECT service_heads.name, SUM(final_amount) as total_final,<br/>
                  SUM(paid_amount) as total_paid<br/>
                  FROM rfq_mst JOIN service_heads_mst<br/>
                  GROUP BY service_head_id
                </code>
              </div>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-warning mb-2">💰 Financial Insights</h6>
              <ul className="mb-3">
                <li><strong>Payment Efficiency:</strong> Shows which services have better payment rates</li>
                <li><strong>Cash Flow Analysis:</strong> Identifies pending payments by service</li>
                <li><strong>Service Profitability:</strong> Correlates payment status with service types</li>
                <li><strong>Risk Assessment:</strong> Highlights services with payment delays</li>
              </ul>

              <h6 className="text-info mb-2">🎯 Key Metrics</h6>
              <ul className="mb-0">
                <li>Green bars show paid amounts</li>
                <li>Blue bars show total final amounts</li>
                <li>Gap between bars indicates pending payments</li>
                <li>Click on bars to view detailed payment breakdown</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'good_service_heads':
      return (
        <div>
          <h5 className="text-primary mb-3">
            <i className="bx bx-bar-chart-alt-2 me-2"></i>Service Heads Performance - Business Logic & Calculation
          </h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">📊 Data Source & Calculation</h6>
              <p className="mb-2"><strong>Primary Data Source:</strong></p>
              <ul className="mb-2">
                <li><strong>Table:</strong> rfq_mst (RFQ Master table)</li>
                <li><strong>Join:</strong> service_heads_mst (Service Heads Master)</li>
                <li><strong>Filter:</strong> Active RFQs only (deleted_at IS NULL)</li>
                <li><strong>User Context:</strong> Based on user's project access permissions</li>
              </ul>

              <p className="mb-2"><strong>Calculation Logic:</strong></p>
              <ul className="mb-2">
                <li>Groups by service_head_id</li>
                <li><strong>Total RFQs:</strong> COUNT(*) per service head</li>
                <li><strong>Completed RFQs:</strong> COUNT(*) WHERE status = 'Closed'</li>
                <li><strong>Completion Rate:</strong> (completed_rfqs / total_rfqs) * 100</li>
                <li><strong>Ranking:</strong> Orders by total RFQ count (top performers first)</li>
              </ul>

              <div className="bg-light p-2 rounded">
                <small className="text-muted d-block mb-1"><strong>SQL Query:</strong></small>
                <code style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                  SELECT service_heads.name, COUNT(*) as total,<br/>
                  COUNT(CASE WHEN status='Closed' THEN 1 END) as completed<br/>
                  FROM rfq_mst JOIN service_heads_mst<br/>
                  GROUP BY service_head_id
                </code>
              </div>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-success mb-2">📈 Performance Metrics</h6>
              <ul className="mb-3">
                <li><strong>Volume Performance:</strong> Shows which services handle most RFQs</li>
                <li><strong>Completion Efficiency:</strong> Measures service delivery success rate</li>
                <li><strong>Service Utilization:</strong> Identifies high-demand service areas</li>
                <li><strong>Quality Indicator:</strong> High completion rates suggest good service quality</li>
              </ul>

              <h6 className="text-info mb-2">🎯 Chart Interpretation</h6>
              <ul className="mb-0">
                <li>Bar height shows total RFQ count</li>
                <li>Color intensity indicates completion rate</li>
                <li>Click on bars to view detailed RFQ breakdown</li>
                <li>Top performers appear first (left to right)</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    case 'good_service_budget':
      return (
        <div>
          <h5 className="text-primary mb-3">
            <i className="bx bx-trending-up me-2"></i>Budget vs Final Amount Analysis - Business Logic & Calculation
          </h5>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-primary mb-2">📊 Data Source & Calculation</h6>
              <p className="mb-2"><strong>Primary Data Source:</strong></p>
              <ul className="mb-2">
                <li><strong>Table:</strong> rfq_mst (RFQ Master table)</li>
                <li><strong>Join:</strong> service_heads_mst (Service Heads Master)</li>
                <li><strong>Filter:</strong> Active RFQs with budget data (deleted_at IS NULL, budget &gt; 0)</li>
                <li><strong>User Context:</strong> Based on user's project access permissions</li>
              </ul>

              <p className="mb-2"><strong>Calculation Logic:</strong></p>
              <ul className="mb-2">
                <li>Groups by service_head_id</li>
                <li><strong>Total Budget:</strong> SUM(budget) per service head</li>
                <li><strong>Total Final Amount:</strong> SUM(final_amount) per service head</li>
                <li><strong>Budget Variance:</strong> final_amount - budget</li>
                <li><strong>Variance Percentage:</strong> ((final_amount - budget) / budget) * 100</li>
              </ul>

              <div className="bg-light p-2 rounded">
                <small className="text-muted d-block mb-1"><strong>SQL Query:</strong></small>
                <code style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                  SELECT service_heads.name, SUM(budget) as total_budget,<br/>
                  SUM(final_amount) as total_final<br/>
                  FROM rfq_mst JOIN service_heads_mst<br/>
                  GROUP BY service_head_id
                </code>
              </div>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardBody>
              <h6 className="text-warning mb-2">💼 Budget Analysis</h6>
              <ul className="mb-3">
                <li><strong>Cost Control:</strong> Shows which services stay within budget</li>
                <li><strong>Budget Accuracy:</strong> Measures estimation vs actual costs</li>
                <li><strong>Service Efficiency:</strong> Identifies cost-effective service areas</li>
                <li><strong>Financial Planning:</strong> Helps improve future budget estimates</li>
              </ul>

              <h6 className="text-info mb-2">🎯 Chart Interpretation</h6>
              <ul className="mb-0">
                <li>Blue bars show budgeted amounts</li>
                <li>Orange bars show actual final amounts</li>
                <li>When orange &gt; blue: over-budget (cost overrun)</li>
                <li>When blue &gt; orange: under-budget (cost savings)</li>
                <li>Click on bars to view detailed budget breakdown</li>
              </ul>
            </CardBody>
          </Card>
        </div>
      )

    default:
      return (
        <div>
          <h5 className="text-primary mb-3">Data Calculation Information</h5>
          <p>Detailed calculation logic for this section is being prepared.</p>
        </div>
      )
  }
}

export default InfoModal
